import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search,
  Send,
  Megaphone,
  Plus,
  X,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  Bot,
  Sparkles,
  Edit3,
  ArrowLeft,
} from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import TemplateHintsEditor from "@/components/TemplateHintsEditor";
import ReengagementSettings from "@/components/ReengagementSettings";
import OptedOutStudents from "@/components/OptedOutStudents";
import { Settings2 } from "lucide-react";

// ---------- types ----------

interface Student {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  grade?: string; // class_10, class_12, under_graduate, etc.
}

const GRADE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "All grades" },
  { value: "class_10", label: "10th" },
  { value: "class_11", label: "11th" },
  { value: "class_12", label: "12th" },
  { value: "under_graduate", label: "UG" },
  { value: "post_graduate", label: "PG" },
  { value: "diploma", label: "Diploma" },
  { value: "other", label: "Other" },
];

const PAGE_SIZE = 50;

interface ApiCampaign {
  id: string;
  name: string;
  status?: string;
  type?: string;
  messageType?: string;
  aisensyTemplateId?: string;
  templateName?: string;
  templateId?: string; // local WhatsappTemplate.id (FK)
}

interface Template {
  id: string;
  name: string;
  label?: string;
  status: string;
  category?: string;
  type?: string;
  language?: string;
  text?: string;
  sampleText?: string;
  totalParameters: number;
  quickReplies?: string[];
  callToAction?: Array<{ type: string; button_value: string; button_title: string }>;
  paramHints?: Array<{ index: number; label: string; hint?: string }>;
  rejectedReason?: string;
}

interface VetterRow {
  index: number;
  userId?: string;
  destination: string;
  userName: string;
  fit: "good" | "skip" | "error";
  reasoning: string;
  suggestedParams: string[];
  renderedPreview?: string;
  errorMessage?: string;
  sendLogId?: string;
}

interface VetResponse {
  batchId: string;
  template: Pick<
    Template,
    "id" | "name" | "text" | "sampleText" | "totalParameters" | "paramHints"
  >;
  results: VetterRow[];
  summary: { good: number; skip: number; error: number };
}

interface SendResult {
  batchId: string;
  totalRequested: number;
  totalSent: number;
  totalFailed: number;
  results: Array<{
    sendLogId: string;
    destination: string;
    status: "sent" | "failed";
    messageId?: string;
    error?: string;
  }>;
}

interface ManualRecipient {
  id: string;
  destination: string;
  userName: string;
}

// ---------- helpers ----------

const renderPreview = (text: string | undefined, params: string[]): string => {
  if (!text) return "";
  return text.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n) => {
    const idx = Number(n) - 1;
    return params[idx] ?? `{{${n}}}`;
  });
};

// ---------- component ----------

type Phase = "pick" | "review" | "done";

const Campaigns: React.FC = () => {
  // Phase
  const [phase, setPhase] = useState<Phase>("pick");

  // ---- Step 1 (pick) state ----
  const [templates, setTemplates] = useState<Template[]>([]);
  const [campaigns, setCampaigns] = useState<ApiCampaign[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<ApiCampaign | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [editingHintsTemplate, setEditingHintsTemplate] = useState<Template | null>(null);

  const [students, setStudents] = useState<Student[]>([]);
  const [studentSearch, setStudentSearch] = useState("");
  const [studentSearchDebounced, setStudentSearchDebounced] = useState("");
  const [gradeFilter, setGradeFilter] = useState<string>("");
  const [phoneOnly, setPhoneOnly] = useState<boolean>(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentPage, setStudentPage] = useState(1);
  const [studentLastPage, setStudentLastPage] = useState(1);
  const [studentTotal, setStudentTotal] = useState(0);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [manualRecipients, setManualRecipients] = useState<ManualRecipient[]>([]);

  const [vetting, setVetting] = useState(false);
  const [vetError, setVetError] = useState<string | null>(null);

  // ---- Step 2 (review) state ----
  const [vetResponse, setVetResponse] = useState<VetResponse | null>(null);
  const [editedParams, setEditedParams] = useState<Record<string, string[]>>({});
  const [includedIds, setIncludedIds] = useState<Set<string>>(new Set());

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendResult, setSendResult] = useState<SendResult | null>(null);

  // ---- Fetch templates + students ----

  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const resp = await api.get<{ items: Template[] }>(
        `/admin/whatsapp/templates?limit=100`,
      );
      const items = (resp as any)?.items ?? (resp as any) ?? [];
      setTemplates(Array.isArray(items) ? items : []);
    } catch {
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      const resp = await api.get<{ items: ApiCampaign[] }>(
        `/admin/whatsapp/api-campaigns?limit=200`,
      );
      const items =
        (resp as any)?.items ??
        (resp as any)?.data?.items ??
        (resp as any) ??
        [];
      setCampaigns(Array.isArray(items) ? items : []);
    } catch {
      setCampaigns([]);
    } finally {
      setLoadingCampaigns(false);
    }
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      // Sync both — templates first so campaigns can FK to them.
      await api.post(`/admin/whatsapp/templates/sync`, {});
      await api.post(`/admin/whatsapp/api-campaigns/sync`, {});
      await Promise.all([fetchTemplates(), fetchCampaigns()]);
    } catch (err: any) {
      setVetError(err?.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  // An approved template with no Live campaign wrapping it can't be sent —
  // the picker below only lists campaigns, so such a template is otherwise
  // invisible here with no clue why. Creates the campaign on AiSensy, mirrors
  // it locally, then selects it so the admin lands straight in the flow.
  const handleCreateCampaign = async (
    template: Template,
    campaignName: string,
  ) => {
    const resp = await api.post<any>(`/admin/whatsapp/api-campaigns/create`, {
      templateName: template.name,
      campaignName,
    });
    const created: ApiCampaign = (resp as any)?.data ?? resp;
    await fetchCampaigns();
    setSelectedCampaign(created);
  };

  useEffect(() => {
    void fetchTemplates();
    void fetchCampaigns();
  }, [fetchTemplates, fetchCampaigns]);

  // When admin picks a campaign, auto-resolve its underlying template so
  // preview + hints + vetter all work as before.
  useEffect(() => {
    if (!selectedCampaign) {
      setSelectedTemplate(null);
      return;
    }
    const t =
      (selectedCampaign.templateId &&
        templates.find((x) => x.id === selectedCampaign.templateId)) ||
      templates.find((x) => x.name === selectedCampaign.templateName) ||
      null;
    setSelectedTemplate(t);
  }, [selectedCampaign, templates]);

  useEffect(() => {
    const t = setTimeout(() => setStudentSearchDebounced(studentSearch), 350);
    return () => clearTimeout(t);
  }, [studentSearch]);

  const fetchStudentsPage = useCallback(
    async (page: number, append: boolean) => {
      setLoadingStudents(true);
      try {
        const params: Record<string, string> = {
          page: String(page),
          limit: String(PAGE_SIZE),
        };
        if (studentSearchDebounced) params.search = studentSearchDebounced;
        const resp = await api.get<any>(
          `/admin/students?${new URLSearchParams(params).toString()}`,
        );
        // api wrapper unwraps shape — handle { data, meta } / bare array
        const rows: Student[] = Array.isArray(resp)
          ? resp
          : resp?.data ?? [];
        const meta = resp?.meta;
        setStudents((prev) => (append ? [...prev, ...rows] : rows));
        if (meta?.lastPage) setStudentLastPage(meta.lastPage);
        if (typeof meta?.total === "number") setStudentTotal(meta.total);
      } catch {
        if (!append) setStudents([]);
      } finally {
        setLoadingStudents(false);
      }
    },
    [studentSearchDebounced],
  );

  // Reset to page 1 whenever search changes
  useEffect(() => {
    setStudentPage(1);
    void fetchStudentsPage(1, false);
  }, [fetchStudentsPage]);

  // Infinite-scroll handler — load next page when near bottom of the list
  const handleStudentsScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
      if (
        nearBottom &&
        !loadingStudents &&
        studentPage < studentLastPage
      ) {
        const next = studentPage + 1;
        setStudentPage(next);
        void fetchStudentsPage(next, true);
      }
    },
    [loadingStudents, studentPage, studentLastPage, fetchStudentsPage],
  );

  // Client-side filter (grade + has-phone toggle)
  const visibleStudents = useMemo(() => {
    return students.filter((s) => {
      if (phoneOnly && !s.phoneNumber) return false;
      if (gradeFilter && s.grade !== gradeFilter) return false;
      return true;
    });
  }, [students, phoneOnly, gradeFilter]);

  const studentsByUserId = useMemo(() => {
    const m = new Map<string, Student>();
    students.forEach((s) => m.set(s.userId, s));
    return m;
  }, [students]);

  const totalRecipients = selectedUserIds.size + manualRecipients.length;

  // ---- Step 1 actions ----

  const selectAllVisible = () => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      visibleStudents.forEach((s) => {
        if (s.phoneNumber) next.add(s.userId);
      });
      return next;
    });
  };

  const clearStudentSelection = () => setSelectedUserIds(new Set());

  const toggleStudent = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const addManualRecipient = () =>
    setManualRecipients((p) => [
      ...p,
      { id: `m-${Date.now()}-${p.length}`, destination: "+91", userName: "" },
    ]);

  const updateManualRecipient = (id: string, patch: Partial<ManualRecipient>) =>
    setManualRecipients((p) => p.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const removeManualRecipient = (id: string) =>
    setManualRecipients((p) => p.filter((r) => r.id !== id));

  const canVet =
    !!selectedTemplate &&
    selectedTemplate.status === "APPROVED" &&
    totalRecipients > 0 &&
    !vetting;

  const handleVet = async () => {
    if (!canVet || !selectedTemplate) return;
    setVetting(true);
    setVetError(null);
    try {
      const recipients: any[] = [];
      selectedUserIds.forEach((userId) => recipients.push({ userId }));
      manualRecipients.forEach((r) => {
        const dest = r.destination.trim();
        if (!dest || dest === "+91") return;
        recipients.push({
          destination: dest,
          userName: r.userName.trim() || "there",
        });
      });
      if (recipients.length === 0) {
        setVetError("Add at least one valid recipient");
        setVetting(false);
        return;
      }
      const resp: VetResponse = await api.post<VetResponse>(
        `/admin/whatsapp/campaigns/vet`,
        {
          templateId: selectedTemplate.id,
          // When admin picked an actual Campaign (Step 1 dropdown), the AiSensy
          // send needs that exact campaign_name (templates and campaigns are
          // distinct objects in AiSensy). Otherwise fall back to template name.
          campaignName: selectedCampaign?.name || selectedTemplate.name,
          recipients,
        },
      );
      const initialParams: Record<string, string[]> = {};
      const initialIncluded = new Set<string>();
      resp.results.forEach((r: VetterRow) => {
        if (r.sendLogId) {
          initialParams[r.sendLogId] = r.suggestedParams;
          if (r.fit === "good") initialIncluded.add(r.sendLogId);
        }
      });
      setVetResponse(resp);
      setEditedParams(initialParams);
      setIncludedIds(initialIncluded);
      setPhase("review");
    } catch (err: any) {
      setVetError(err?.message || "Vetting failed");
    } finally {
      setVetting(false);
    }
  };

  // ---- Step 2 actions ----

  const updateRowParam = (sendLogId: string, paramIdx: number, value: string) => {
    setEditedParams((prev) => {
      const arr = [...(prev[sendLogId] || [])];
      arr[paramIdx] = value;
      return { ...prev, [sendLogId]: arr };
    });
  };

  const toggleIncluded = (sendLogId: string) => {
    setIncludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sendLogId)) next.delete(sendLogId);
      else next.add(sendLogId);
      return next;
    });
  };

  const handleConfirmSend = async () => {
    if (!vetResponse || includedIds.size === 0) return;
    setSending(true);
    setSendError(null);
    try {
      const rows = Array.from(includedIds).map((id) => ({
        sendLogId: id,
        templateParams: editedParams[id] || [],
      }));
      const resp = await api.post<SendResult>(
        `/admin/whatsapp/campaigns/send-vetted`,
        { batchId: vetResponse.batchId, rows },
      );
      setSendResult(resp);
      setPhase("done");
    } catch (err: any) {
      setSendError(err?.message || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const resetAll = () => {
    setPhase("pick");
    setVetResponse(null);
    setSendResult(null);
    setSendError(null);
    setVetError(null);
    setEditedParams({});
    setIncludedIds(new Set());
    setSelectedUserIds(new Set());
    setManualRecipients([]);
    setSelectedTemplate(null);
  };

  // ---------- render ----------

  return (
    <div className="max-w-6xl mx-auto">
      <Header phase={phase} onBack={phase !== "pick" ? resetAll : undefined} />

      {phase === "pick" && (
        <PickStep
          templates={templates}
          campaigns={campaigns}
          loadingTemplates={loadingTemplates}
          loadingCampaigns={loadingCampaigns}
          syncing={syncing}
          onSync={handleSync}
          onCreateCampaign={handleCreateCampaign}
          selectedCampaign={selectedCampaign}
          onSelectCampaign={setSelectedCampaign}
          selectedTemplate={selectedTemplate}
          onSelectTemplate={setSelectedTemplate}
          onEditHints={(t) => setEditingHintsTemplate(t)}
          students={students}
          visibleStudents={visibleStudents}
          studentSearch={studentSearch}
          onStudentSearchChange={setStudentSearch}
          gradeFilter={gradeFilter}
          onGradeFilterChange={setGradeFilter}
          phoneOnly={phoneOnly}
          onPhoneOnlyChange={setPhoneOnly}
          loadingStudents={loadingStudents}
          studentTotal={studentTotal}
          studentPage={studentPage}
          studentLastPage={studentLastPage}
          onStudentsScroll={handleStudentsScroll}
          selectedUserIds={selectedUserIds}
          onToggleStudent={toggleStudent}
          onSelectAllVisible={selectAllVisible}
          onClearStudentSelection={clearStudentSelection}
          studentsByUserId={studentsByUserId}
          manualRecipients={manualRecipients}
          onAddManual={addManualRecipient}
          onUpdateManual={updateManualRecipient}
          onRemoveManual={removeManualRecipient}
          totalRecipients={totalRecipients}
          canVet={canVet}
          vetting={vetting}
          vetError={vetError}
          onVet={handleVet}
        />
      )}

      {/* Template creation and AI suggestions live on their own page
          (/templates) — this page is for running a campaign, and library
          maintenance mid-send is a distraction. */}
      {phase === "pick" && <ReengagementSettings />}
      {phase === "pick" && <OptedOutStudents />}

      {phase === "review" && vetResponse && (
        <ReviewStep
          vetResponse={vetResponse}
          editedParams={editedParams}
          includedIds={includedIds}
          onUpdateParam={updateRowParam}
          onToggleIncluded={toggleIncluded}
          sending={sending}
          sendError={sendError}
          onConfirmSend={handleConfirmSend}
        />
      )}

      {phase === "done" && sendResult && (
        <DoneStep result={sendResult} onSendAnother={resetAll} />
      )}

      {editingHintsTemplate && (
        <TemplateHintsEditor
          template={editingHintsTemplate as any}
          onClose={() => setEditingHintsTemplate(null)}
          onSaved={(updated) => {
            // refresh the selected template's hints inline + the catalog
            setSelectedTemplate((prev) =>
              prev && prev.id === updated.id
                ? { ...prev, ...(updated as any) }
                : prev,
            );
            setTemplates((prev) =>
              prev.map((t) => (t.id === updated.id ? ({ ...t, ...(updated as any) }) : t)),
            );
          }}
        />
      )}
    </div>
  );
};

// ---------- subcomponents ----------

const Header: React.FC<{ phase: Phase; onBack?: () => void }> = ({
  phase,
  onBack,
}) => (
  <div className="flex items-center justify-between mb-6">
    <div className="flex items-center gap-3">
      {onBack && (
        <button
          onClick={onBack}
          className="text-gray-500 hover:text-gray-900 mr-1"
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
      )}
      <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
        <Megaphone className="h-5 w-5 text-orange-600" />
      </div>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">WhatsApp Campaigns</h1>
        <p className="text-sm text-gray-500">
          {phase === "pick" && "Pick a template + recipients. AI will vet each before send."}
          {phase === "review" && "Review AI suggestions, edit params, confirm to send."}
          {phase === "done" && "Send complete."}
        </p>
      </div>
    </div>
    <StepIndicator phase={phase} />
  </div>
);

const StepIndicator: React.FC<{ phase: Phase }> = ({ phase }) => {
  const steps = [
    { id: "pick", label: "1. Pick" },
    { id: "review", label: "2. Vet & review" },
    { id: "done", label: "3. Send" },
  ];
  return (
    <div className="hidden sm:flex items-center gap-2">
      {steps.map((s, i) => {
        const isActive = phase === s.id;
        const isDone =
          (phase === "review" && s.id === "pick") ||
          (phase === "done" && (s.id === "pick" || s.id === "review"));
        return (
          <React.Fragment key={s.id}>
            <span
              className={cn(
                "px-3 py-1 rounded-full text-xs font-medium",
                isActive
                  ? "bg-orange-500 text-white"
                  : isDone
                    ? "bg-orange-100 text-orange-800"
                    : "bg-gray-100 text-gray-500",
              )}
            >
              {s.label}
            </span>
            {i < steps.length - 1 && (
              <span className="text-gray-300">›</span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

interface PickStepProps {
  templates: Template[];
  campaigns: ApiCampaign[];
  loadingTemplates: boolean;
  loadingCampaigns: boolean;
  syncing: boolean;
  onSync: () => void;
  onCreateCampaign: (template: Template, campaignName: string) => Promise<void>;
  selectedCampaign: ApiCampaign | null;
  onSelectCampaign: (c: ApiCampaign | null) => void;
  selectedTemplate: Template | null;
  onSelectTemplate: (t: Template | null) => void;
  onEditHints: (t: Template) => void;
  students: Student[];
  visibleStudents: Student[];
  studentSearch: string;
  onStudentSearchChange: (s: string) => void;
  gradeFilter: string;
  onGradeFilterChange: (g: string) => void;
  phoneOnly: boolean;
  onPhoneOnlyChange: (v: boolean) => void;
  loadingStudents: boolean;
  studentTotal: number;
  studentPage: number;
  studentLastPage: number;
  onStudentsScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  selectedUserIds: Set<string>;
  onToggleStudent: (id: string) => void;
  onSelectAllVisible: () => void;
  onClearStudentSelection: () => void;
  studentsByUserId: Map<string, Student>;
  manualRecipients: ManualRecipient[];
  onAddManual: () => void;
  onUpdateManual: (id: string, patch: Partial<ManualRecipient>) => void;
  onRemoveManual: (id: string) => void;
  totalRecipients: number;
  canVet: boolean;
  vetting: boolean;
  vetError: string | null;
  onVet: () => void;
}

const PickStep: React.FC<PickStepProps> = ({
  templates,
  campaigns,
  loadingTemplates: _loadingTemplates,
  loadingCampaigns,
  syncing,
  onSync,
  onCreateCampaign,
  selectedCampaign,
  onSelectCampaign,
  selectedTemplate,
  onSelectTemplate: _onSelectTemplate,
  onEditHints,
  students,
  visibleStudents,
  studentSearch,
  onStudentSearchChange,
  gradeFilter,
  onGradeFilterChange,
  phoneOnly,
  onPhoneOnlyChange,
  loadingStudents,
  studentTotal,
  studentPage,
  studentLastPage,
  onStudentsScroll,
  selectedUserIds,
  onToggleStudent,
  onSelectAllVisible,
  onClearStudentSelection,
  studentsByUserId,
  manualRecipients,
  onAddManual,
  onUpdateManual,
  onRemoveManual,
  totalRecipients,
  canVet,
  vetting,
  vetError,
  onVet,
}) => {
  // Approved templates with no sendable campaign wrapping them yet — the
  // picker above only lists campaigns, so these would otherwise be invisible
  // here with no clue why "an approved template" can't be sent.
  const orphanedTemplates = templates.filter((t) => {
    if (t.status !== "APPROVED") return false;
    return !campaigns.some((c) => {
      const linked = (c.templateId && c.templateId === t.id) || c.templateName === t.name;
      if (!linked) return false;
      return !c.status || c.status === "LIVE" || c.status === "SENT" || c.status === "RUNNING";
    });
  });

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Campaign picker */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Campaign</h2>
            <button
              type="button"
              onClick={onSync}
              disabled={syncing}
              className="inline-flex items-center gap-1 text-xs text-orange-600 hover:underline disabled:opacity-50"
            >
              <RefreshCw className={cn("h-3 w-3", syncing && "animate-spin")} />
              {syncing ? "Syncing..." : "Sync from AiSensy"}
            </button>
          </div>

          {loadingCampaigns ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="md" />
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">
              No campaigns yet — click <strong>Sync from AiSensy</strong> to pull them.
              <br />
              <span className="text-xs text-gray-400">
                Campaigns live under AiSensy Dashboard → Manage → Campaigns. The
                send API needs an existing campaign — templates alone aren't enough.
              </span>
            </div>
          ) : (
            <div className="space-y-1 max-h-72 overflow-y-auto border border-gray-200 rounded-md">
              {campaigns.map((c) => {
                const isSelected = selectedCampaign?.id === c.id;
                const linkedTpl =
                  (c.templateId && templates.find((t) => t.id === c.templateId)) ||
                  templates.find((t) => t.name === c.templateName);
                const sendable =
                  !c.status ||
                  c.status === "LIVE" ||
                  c.status === "SENT" ||
                  c.status === "RUNNING";
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => sendable && onSelectCampaign(c)}
                    disabled={!sendable}
                    className={cn(
                      "w-full flex items-start gap-3 px-3 py-2 text-left text-sm border-b border-gray-100 last:border-0 transition-colors",
                      isSelected
                        ? "bg-orange-50"
                        : sendable
                          ? "hover:bg-gray-50"
                          : "opacity-50 cursor-not-allowed bg-gray-50",
                    )}
                  >
                    <input
                      type="radio"
                      checked={isSelected}
                      readOnly
                      disabled={!sendable}
                      className="mt-1 accent-orange-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 truncate">
                          {c.name}
                        </span>
                        <StatusBadge status={c.status || "UNKNOWN"} />
                        {c.type && (
                          <span className="text-xs text-gray-400">{c.type}</span>
                        )}
                      </div>
                      {linkedTpl?.text && (
                        <p className="text-xs text-gray-600 line-clamp-2 mt-0.5">
                          {linkedTpl.text}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">
                        Template:{" "}
                        <span className="font-mono">{c.templateName || "—"}</span>
                        {linkedTpl?.totalParameters
                          ? ` · ${linkedTpl.totalParameters} variable${linkedTpl.totalParameters === 1 ? "" : "s"}`
                          : ""}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {orphanedTemplates.length > 0 && (
            <div className="mt-4 border-t border-gray-100 pt-3">
              <p className="text-xs font-medium text-gray-500 mb-2">
                {orphanedTemplates.length} approved template
                {orphanedTemplates.length === 1 ? "" : "s"} with no campaign yet
                — create one to make it sendable
              </p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {orphanedTemplates.map((t) => (
                  <OrphanTemplateRow
                    key={t.id}
                    template={t}
                    onCreate={onCreateCampaign}
                  />
                ))}
              </div>
            </div>
          )}

          {selectedTemplate && (
            <div className="mt-4 rounded-md border border-orange-200 bg-orange-50 p-3">
              <div className="flex items-start justify-between mb-1 gap-2">
                <p className="text-xs font-medium text-orange-800">
                  Template preview (sample)
                </p>
                {selectedTemplate.totalParameters > 0 && (
                  <button
                    type="button"
                    onClick={() => onEditHints(selectedTemplate)}
                    className="inline-flex items-center gap-1 text-xs text-orange-700 hover:text-orange-900 hover:underline"
                  >
                    <Settings2 className="h-3 w-3" />
                    Map {selectedTemplate.totalParameters} parameter
                    {selectedTemplate.totalParameters === 1 ? "" : "s"}
                  </button>
                )}
              </div>
              <p className="text-sm whitespace-pre-wrap text-gray-800">
                {selectedTemplate.sampleText || selectedTemplate.text}
              </p>
              {!!selectedTemplate.quickReplies?.length && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {selectedTemplate.quickReplies.map((q, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 rounded-full bg-white border border-orange-200 text-xs text-orange-700"
                    >
                      {q}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recipients */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Recipients</h2>
            <span className="text-sm text-gray-600">
              {totalRecipients} selected
            </span>
          </div>

          {/* Filters */}
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={studentSearch}
                onChange={(e) => onStudentSearchChange(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
            <select
              value={gradeFilter}
              onChange={(e) => onGradeFilterChange(e.target.value)}
              className="rounded-md border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            >
              {GRADE_OPTIONS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
          </div>

          {/* Bulk actions + counts */}
          <div className="flex items-center justify-between mb-2 text-xs">
            <label className="inline-flex items-center gap-1.5 text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={phoneOnly}
                onChange={(e) => onPhoneOnlyChange(e.target.checked)}
                className="h-3.5 w-3.5 accent-orange-500"
              />
              With phone only
            </label>
            <div className="flex items-center gap-3">
              <span className="text-gray-500">
                Loaded {students.length}
                {studentTotal > 0 ? ` of ${studentTotal}` : ""}
                {visibleStudents.length !== students.length &&
                  ` · ${visibleStudents.length} after filter`}
              </span>
              <button
                type="button"
                onClick={onSelectAllVisible}
                disabled={visibleStudents.length === 0}
                className={cn(
                  "px-2 py-1 rounded text-xs font-medium",
                  visibleStudents.length > 0
                    ? "text-orange-600 hover:bg-orange-50"
                    : "text-gray-300 cursor-not-allowed",
                )}
              >
                Select all visible
              </button>
              {selectedUserIds.size > 0 && (
                <button
                  type="button"
                  onClick={onClearStudentSelection}
                  className="px-2 py-1 rounded text-xs font-medium text-gray-500 hover:bg-gray-100"
                >
                  Clear ({selectedUserIds.size})
                </button>
              )}
            </div>
          </div>

          {/* Scrollable list with infinite scroll */}
          <div
            onScroll={onStudentsScroll}
            className="border border-gray-200 rounded-md max-h-80 overflow-y-auto mb-4"
          >
            {loadingStudents && students.length === 0 ? (
              <div className="flex justify-center py-6">
                <LoadingSpinner size="sm" />
              </div>
            ) : visibleStudents.length === 0 ? (
              <p className="text-center text-sm text-gray-400 py-6">
                No students match the filter
              </p>
            ) : (
              <>
                {visibleStudents.map((s) => {
                  const checked = selectedUserIds.has(s.userId);
                  const hasPhone = !!s.phoneNumber;
                  return (
                    <button
                      type="button"
                      key={s.id}
                      disabled={!hasPhone}
                      onClick={() => onToggleStudent(s.userId)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 text-left text-sm border-b border-gray-100 last:border-0 transition-colors",
                        checked
                          ? "bg-orange-50"
                          : hasPhone
                            ? "hover:bg-gray-50"
                            : "opacity-50 cursor-not-allowed",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        readOnly
                        disabled={!hasPhone}
                        className="h-4 w-4 accent-orange-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="truncate font-medium text-gray-900">
                          {s.firstName} {s.lastName}
                          {s.grade && (
                            <span className="ml-2 text-[10px] text-gray-400 font-normal">
                              {gradeShortLabel(s.grade)}
                            </span>
                          )}
                        </div>
                        <div className="truncate text-xs text-gray-500">
                          {s.phoneNumber || "no phone on file"}
                        </div>
                      </div>
                    </button>
                  );
                })}
                {loadingStudents && students.length > 0 && (
                  <div className="flex justify-center py-3">
                    <LoadingSpinner size="sm" />
                  </div>
                )}
                {!loadingStudents && studentPage >= studentLastPage && students.length > 0 && (
                  <p className="text-center text-[11px] text-gray-400 py-2">
                    End of list
                  </p>
                )}
              </>
            )}
          </div>

          {selectedUserIds.size > 0 && (
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-600 mb-1">
                Selected students
              </p>
              <div className="flex flex-wrap gap-1.5">
                {Array.from(selectedUserIds).map((uid) => {
                  const s = studentsByUserId.get(uid);
                  return (
                    <span
                      key={uid}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-800"
                    >
                      {s ? `${s.firstName} ${s.lastName}` : uid.slice(-8)}
                      <button
                        type="button"
                        onClick={() => onToggleStudent(uid)}
                        className="hover:text-orange-900"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-gray-700">
                Manual numbers
              </label>
              <button
                type="button"
                onClick={onAddManual}
                className="text-xs text-orange-600 hover:underline flex items-center gap-1"
              >
                <Plus className="h-3 w-3" /> Add number
              </button>
            </div>
            <div className="space-y-2">
              {manualRecipients.map((r) => (
                <div key={r.id} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={r.destination}
                    onChange={(e) =>
                      onUpdateManual(r.id, { destination: e.target.value })
                    }
                    placeholder="+919876543210"
                    className="w-44 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                  <input
                    type="text"
                    value={r.userName}
                    onChange={(e) =>
                      onUpdateManual(r.id, { userName: e.target.value })
                    }
                    placeholder="Recipient name"
                    className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                  <button
                    type="button"
                    onClick={() => onRemoveManual(r.id)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="text-sm text-gray-600">
          {selectedTemplate ? (
            <>
              Will AI-vet <span className="font-semibold">{totalRecipients}</span>{" "}
              recipient{totalRecipients === 1 ? "" : "s"} for{" "}
              <span className="font-semibold">{selectedTemplate.name}</span>
            </>
          ) : (
            "Pick a template and at least one recipient to continue"
          )}
        </div>
        <button
          type="button"
          onClick={onVet}
          disabled={!canVet}
          className={cn(
            "inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-white text-sm font-medium transition-colors",
            canVet
              ? "bg-orange-500 hover:bg-orange-600"
              : "bg-gray-300 cursor-not-allowed",
          )}
        >
          {vetting ? <LoadingSpinner size="sm" /> : <Sparkles className="h-4 w-4" />}
          {vetting ? "Vetting..." : "Vet recipients"}
        </button>
      </div>

      {vetError && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <p className="text-sm text-red-700">{vetError}</p>
        </div>
      )}
    </>
  );
};

/**
 * One approved-template-with-no-campaign row. Kept as its own component (not
 * inlined in PickStep's map) so its name-input state doesn't reset on every
 * re-render of the surrounding list.
 */
const OrphanTemplateRow: React.FC<{
  template: Template;
  onCreate: (template: Template, campaignName: string) => Promise<void>;
}> = ({ template, onCreate }) => {
  const [name, setName] = useState(template.name);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Campaign name is required");
      return;
    }
    setCreating(true);
    setError(null);
    try {
      await onCreate(template, trimmed);
    } catch (err: any) {
      setError(err?.message || "Could not create campaign");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-md px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs text-gray-700 truncate flex-1">
          {template.name}
        </span>
        <span className="text-xs text-gray-400">
          {template.totalParameters} var{template.totalParameters === 1 ? "" : "s"}
        </span>
      </div>
      <div className="flex items-center gap-1.5 mt-1.5">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Campaign name"
          disabled={creating}
          className="flex-1 min-w-0 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:opacity-50"
        />
        <button
          type="button"
          onClick={handleCreate}
          disabled={creating}
          className="shrink-0 inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
        >
          {creating ? "Creating…" : "Create campaign"}
        </button>
      </div>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
};

interface ReviewStepProps {
  vetResponse: VetResponse;
  editedParams: Record<string, string[]>;
  includedIds: Set<string>;
  onUpdateParam: (sendLogId: string, paramIdx: number, value: string) => void;
  onToggleIncluded: (sendLogId: string) => void;
  sending: boolean;
  sendError: string | null;
  onConfirmSend: () => void;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  vetResponse,
  editedParams,
  includedIds,
  onUpdateParam,
  onToggleIncluded,
  sending,
  sendError,
  onConfirmSend,
}) => {
  const { template, results, summary } = vetResponse;
  const willSend = includedIds.size;
  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
        <Stat label="Vetted" value={results.length} tint="gray" />
        <Stat label="AI: good fit" value={summary.good} tint="green" />
        <Stat label="AI: skip" value={summary.skip} tint="amber" />
        <Stat label="AI: error" value={summary.error} tint="red" />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-sm text-gray-700">
            Template: <span className="font-mono">{template.name}</span>
          </h2>
          {template.text && (
            <p className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">
              {template.text}
            </p>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100">
          {results.map((r) => {
            const params = (r.sendLogId && editedParams[r.sendLogId]) || r.suggestedParams;
            const included = !!r.sendLogId && includedIds.has(r.sendLogId);
            const preview = renderPreview(template.text, params);
            return (
              <div
                key={`${r.index}-${r.destination}`}
                className={cn(
                  "px-4 py-3 grid grid-cols-1 md:grid-cols-12 gap-3",
                  r.fit === "skip" && !included && "bg-amber-50/40",
                  r.fit === "error" && "bg-red-50/40",
                )}
              >
                <div className="flex items-center gap-2 md:col-span-1 md:flex-col md:items-start md:pt-1">
                  <input
                    type="checkbox"
                    checked={included}
                    disabled={!r.sendLogId || r.fit === "error"}
                    onChange={() => r.sendLogId && onToggleIncluded(r.sendLogId)}
                    className="h-4 w-4 accent-orange-500"
                  />
                  <span className="text-xs text-gray-500 md:hidden">
                    {included ? "Included" : "Excluded"}
                  </span>
                </div>
                <div className="md:col-span-3">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {r.userName?.trim() || (
                      <span className="text-amber-700 inline-flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        No name on file
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 font-mono truncate">
                    {r.destination}
                  </div>
                  <FitBadge fit={r.fit} />
                  <p className="text-xs text-gray-600 mt-1 italic line-clamp-3">
                    {r.reasoning}
                  </p>
                </div>
                <div className="md:col-span-4">
                  <p className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                    <Edit3 className="h-3 w-3" /> Params (editable)
                  </p>
                  {template.totalParameters === 0 ? (
                    <p className="text-xs text-gray-400">No variables in this template</p>
                  ) : (
                    Array.from({ length: template.totalParameters }).map((_, i) => {
                      const hint = template.paramHints?.find(
                        (h) => h.index === i + 1,
                      );
                      return (
                        <div key={i} className="mb-1">
                          <input
                            type="text"
                            value={params[i] || ""}
                            disabled={!r.sendLogId}
                            onChange={(e) =>
                              r.sendLogId && onUpdateParam(r.sendLogId, i, e.target.value)
                            }
                            placeholder={hint?.label || `{{${i + 1}}}`}
                            className="w-full rounded border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-orange-400 disabled:bg-gray-100"
                          />
                          {hint?.label && (
                            <span className="text-[10px] text-gray-400">
                              {`{{${i + 1}}} — ${hint.label}`}
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="md:col-span-4">
                  <p className="text-xs font-medium text-gray-500 mb-1">Rendered preview</p>
                  <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs whitespace-pre-wrap text-gray-800">
                    {preview || "—"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="text-sm text-gray-600">
          Will send to <span className="font-semibold">{willSend}</span> recipient
          {willSend === 1 ? "" : "s"}
        </div>
        <button
          type="button"
          onClick={onConfirmSend}
          disabled={willSend === 0 || sending}
          className={cn(
            "inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-white text-sm font-medium transition-colors",
            willSend > 0 && !sending
              ? "bg-orange-500 hover:bg-orange-600"
              : "bg-gray-300 cursor-not-allowed",
          )}
        >
          {sending ? <LoadingSpinner size="sm" /> : <Send className="h-4 w-4" />}
          {sending ? "Sending..." : `Confirm send (${willSend})`}
        </button>
      </div>

      {sendError && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <p className="text-sm text-red-700">{sendError}</p>
        </div>
      )}
    </>
  );
};

const DoneStep: React.FC<{ result: SendResult; onSendAnother: () => void }> = ({
  result,
  onSendAnother,
}) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
    <div className="flex items-center justify-between mb-4">
      <div>
        <h3 className="text-lg font-semibold">Send complete</h3>
        <p className="text-sm text-gray-500">
          {result.totalSent}/{result.totalRequested} delivered
          {result.totalFailed > 0 ? `, ${result.totalFailed} failed` : ""}
        </p>
      </div>
      <button
        type="button"
        onClick={onSendAnother}
        className="text-sm text-orange-600 hover:underline"
      >
        Start a new campaign
      </button>
    </div>
    <div className="space-y-1 max-h-80 overflow-y-auto">
      {result.results.map((r) => (
        <div
          key={r.sendLogId}
          className="flex items-center gap-2 text-sm py-1.5 border-b border-gray-100 last:border-0"
        >
          {r.status === "sent" ? (
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          ) : (
            <XCircle className="h-4 w-4 text-red-500" />
          )}
          <span className="font-mono text-xs text-gray-700 w-40 truncate">
            {r.destination}
          </span>
          <span
            className={cn(
              "text-xs",
              r.status === "sent" ? "text-green-700" : "text-red-700",
            )}
          >
            {r.status === "sent"
              ? r.messageId
                ? `sent · ${r.messageId.slice(0, 16)}`
                : "sent"
              : r.error}
          </span>
        </div>
      ))}
    </div>
  </div>
);

// ---------- tiny ui ----------

const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  const tint =
    status === "APPROVED"
      ? "bg-green-100 text-green-700"
      : status === "REJECTED"
        ? "bg-red-100 text-red-700"
        : "bg-amber-100 text-amber-700";
  return (
    <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-medium", tint)}>
      {status || "UNKNOWN"}
    </span>
  );
};

const FitBadge: React.FC<{ fit: "good" | "skip" | "error" }> = ({ fit }) => {
  const map = {
    good: { label: "AI: good fit", className: "bg-green-100 text-green-800" },
    skip: { label: "AI: skip", className: "bg-amber-100 text-amber-800" },
    error: { label: "Vetter error", className: "bg-red-100 text-red-700" },
  };
  const m = map[fit];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium mt-1",
        m.className,
      )}
    >
      <Bot className="h-2.5 w-2.5" />
      {m.label}
    </span>
  );
};

const gradeShortLabel = (g: string): string => {
  const map: Record<string, string> = {
    class_10: "10th",
    class_11: "11th",
    class_12: "12th",
    under_graduate: "UG",
    post_graduate: "PG",
    diploma: "Diploma",
    doctorate: "PhD",
  };
  return map[g] || g.replace(/_/g, " ");
};

const Stat: React.FC<{
  label: string;
  value: number;
  tint: "gray" | "green" | "amber" | "red";
}> = ({ label, value, tint }) => {
  const tintCls = {
    gray: "text-gray-700",
    green: "text-green-700",
    amber: "text-amber-700",
    red: "text-red-700",
  }[tint];
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className={cn("text-xl font-semibold", tintCls)}>{value}</p>
    </div>
  );
};

export default Campaigns;
